import {
  alpha,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getOfferById, getLenderById, updateBorrowerOffer } from "../../API/API";
import { showToast } from "../../components/common/Util/Util";
import { useSelector } from "react-redux";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const PageWrapper = styled(Box)({
  background: "linear-gradient(135deg, #F4F6F8 0%, #E9F3FF 50%, #FDFBFF 100%)",
  minHeight: "100vh",
  padding: "24px 8px",
  margin: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  boxSizing: "border-box",
});

const ContentContainer = styled(Box)({
  backgroundColor: "#FFFFFF",
  width: "100%",
  maxWidth: "980px",
  padding: "32px 40px",
  boxShadow: "0 18px 45px rgba(15, 23, 42, 0.10)",
  border: "1px solid rgba(145, 158, 171, 0.18)",
  margin: "16px",
  borderRadius: "18px",
  position: "relative",
});

const commonTextStyles = {
  fontFamily: "'Open Sans', sans-serif",
  lineHeight: "120%",
  letterSpacing: "0%",
};

const commonButtonStyles = {
  borderRadius: "8px",
  padding: "10px 24px",
  textTransform: "none",
  fontFamily: "'Inter', sans-serif",
  fontSize: "16px",
  fontWeight: 600,
  height: "48px",
  minWidth: "180px",
};

const PageTitle = styled(Typography)({
  ...commonTextStyles,
  color: "#0C53B7",
  fontWeight: 600,
  fontSize: "26px",
  letterSpacing: "0.02em",
  marginBottom: "4px",
});

const PageDescription = styled(Typography)({
  ...commonTextStyles,
  color: "#637381",
  fontSize: "14px",
  fontWeight: 400,
  fontStyle: "italic",
  marginBottom: "20px",
});

const Divider = styled(Box)({
  width: "100%",
  height: "1px",
  backgroundColor: "#E5E8EB",
  margin: "12px 0 24px 0",
});

const SectionTitle = styled(Typography)({
  ...commonTextStyles,
  color: "#111827",
  fontWeight: 600,
  fontSize: "18px",
  letterSpacing: "0.02em",
  marginBottom: "12px",
  marginTop: "28px",
});

const InfoTable = styled(Box)({
  border: "1px solid rgba(145,158,171,0.25)",
  borderRadius: "12px",
  overflow: "hidden",
  background: "linear-gradient(135deg, #FAFBFF 0%, #FFFFFF 60%, #F8FAFF 100%)",
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
});

const InfoRow = styled(Grid)({
  padding: "14px 18px",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(249, 250, 251, 0.7)",
  },
});

const InfoLabel = styled(Typography)({
  ...commonTextStyles,
  color: "#637381",
  fontSize: "14px",
  fontWeight: 500,
});

const InfoValue = styled(Typography)({
  ...commonTextStyles,
  color: "#212B36",
  fontSize: "14px",
  fontWeight: 600,
});

const LoanTableWrapper = styled(Box)(({ theme }) => ({
  "& .MuiTableContainer-root": {
    borderRadius: "16px",
    border: "1px solid rgba(229, 232, 235, 0.7)",
    boxShadow: "0 4px 24px -4px rgba(0, 0, 0, 0.04)",
    overflow: "visible",
    backgroundColor: theme?.palette?.background?.paper || "#FFFFFF",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    maxWidth: "100%",
    "& table": {
      tableLayout: "fixed",
      width: "100%",
    },
    "& th, & td": {
      padding: theme.spacing(1, 1.2),
      wordBreak: "break-word",
    },
    "&:hover": {
      boxShadow: "0 8px 32px -8px rgba(0, 0, 0, 0.06)",
    },
    "&::-webkit-scrollbar": {
      height: "8px",
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#888",
      borderRadius: "4px",
      "&:hover": {
        background: "#666",
      },
    },
  },
}));

const LoanTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 1.5),
  "&.MuiTableCell-head": {
    backgroundColor: "rgba(15, 23, 42, 0.04)",
    color: "#111827",
    fontWeight: 600,
    fontSize: "0.85rem",
    whiteSpace: "nowrap",
    letterSpacing: "0.02em",
    borderBottom: "2px solid rgba(229, 232, 235, 0.9)",
  },
  "&.MuiTableCell-body": {
    color: theme.palette.text.primary,
    fontSize: "0.85rem",
    letterSpacing: "0.01em",
    borderBottom: "1px solid rgba(229, 232, 235, 0.5)",
  },
}));

const LoanTableEmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: "center",
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  borderRadius: "16px",
  border: "1px dashed rgba(0, 0, 0, 0.12)",
  color: theme.palette.text.secondary,
}));

const InstructionItem = styled(Box)({
  marginBottom: "16px",
});

const InstructionTitle = styled(Typography)({
  ...commonTextStyles,
  color: "#212B36",
  fontWeight: 600,
  fontSize: "16px",
  marginBottom: "4px",
});

const InstructionText = styled(Typography)({
  ...commonTextStyles,
  color: "#637381",
  fontSize: "14px",
  fontWeight: 400,
});

const AcceptOfferButton = styled(Button)({
  ...commonButtonStyles,
  backgroundColor: "#0052CC",
  color: "white",
  "&:hover": {
    backgroundColor: "#003E99",
    boxShadow: "0 10px 24px rgba(0, 82, 204, 0.35)",
  },
  boxShadow: "0 8px 20px rgba(0, 82, 204, 0.28)",
});

const DeclineOfferButton = styled(Button)({
  ...commonButtonStyles,
  borderColor: "#FF4842",
  color: "#FF4842",
  "&:hover": {
    borderColor: "#B72136",
    backgroundColor: "rgba(255, 72, 66, 0.06)",
  },
  backgroundColor: "rgba(255, 72, 66, 0.02)",
});

const CheckboxLabel = styled(Typography)({
  ...commonTextStyles,
  color: "#212B36",
  fontSize: "14px",
  fontWeight: 400,
  fontStyle: "italic",
});

const BackButton = styled(Button)({
  color: '#637381',
  textTransform: 'none',
  padding: 0,
  marginBottom: { xs: '8px', sm: '16px' },
  '&:hover': {
    backgroundColor: 'transparent',
    color: '#212B36',
  },
});

const ViewProposalDetails = () => {
  const navigate = useNavigate();
  const { offerId } = useParams();
  const contentRef = useRef(null);
  const watermarkSrc = `${process.env.PUBLIC_URL || ""}/watermark.jpg`;
  const letterheadLogoSrc = `${process.env.PUBLIC_URL || ""}/dequiLogo.png`;
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [offerDetails, setOfferDetails] = useState(null);
  const [lenderDetails, setLenderDetails] = useState(null);
  const userDetail = useSelector((state) => state.user?.user);
  const userId = userDetail?.ID;
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRejectionOption, setSelectedRejectionOption] = useState("");
  const [rejectedReason, setrejectedReason] = useState("");

  const rejectionOptions = [
    "Interest rate too high",
    "Loan amount insufficient",
    "Tenure not suitable",
    "Found better offer",
    "Processing fees too high",
    "Other"
  ];

  const fetchOfferDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getOfferById(offerId);
      if (response?.result) {
        const offerData = {
          ...response.result.result,
          bankType: response.result.bankType,
          bankName: response.result.bankName
        };
        setOfferDetails(offerData);

        if (offerData?.lenderId) {
          try {
            const lenderResponse = await getLenderById(offerData.lenderId);
            if (lenderResponse?.result) {
              setLenderDetails(lenderResponse.result.lender || lenderResponse.result);
            } else {
              setLenderDetails(null);
            }
          } catch (lenderError) {
            console.error("Failed to fetch lender details", lenderError);
            setLenderDetails(null);
          }
        } else {
          setLenderDetails(null);
        }
      } else {
        showToast("Failed to fetch offer details", "error");
        navigate("/accepted-offers");
      }
    } catch (error) {
      console.error(error);
      showToast("Error fetching offer details", "error");
      navigate("/accepted-offers");
    } finally {
      setLoading(false);
    }
  }, [offerId, navigate]);

  useEffect(() => {
    if (offerId) {
      fetchOfferDetails();
    }
  }, [offerId, fetchOfferDetails]);

  const canTakeAction = useMemo(() => {
    const status = offerDetails?.offerStatus?.toLowerCase();
    return status && !["accepted", "rejected", "expired"].includes(status);
  }, [offerDetails?.offerStatus]);

  const handleOfferAction = useCallback(async (status) => {

    if (status === "accepted" && !termsAccepted) {
      showToast("Please accept the terms and conditions", "error");
      return;
    }

    if (status === "rejected") {
      setRejectDialogOpen(true);
      return;
    }

    try {
      setLoading(true);
      await updateBorrowerOffer({
        offerId,
        status,
        borrowerId: userId,
        rejectedReason: status === "rejected" ? "" : undefined
      });
      showToast(
        status === "accepted" ? "Offer accepted successfully!" : "Offer declined.",
        status === "accepted" ? "success" : "info"
      );
      navigate("/accepted-offers");
    } catch (error) {
      console.error(error);
      showToast(`Failed to ${status} offer`, "error");
    } finally {
      setLoading(false);
    }
  }, [offerId, userId, termsAccepted, navigate]);

  const handleReject = async () => {
    try {
      setLoading(true);
      const finalrejectedReason = selectedRejectionOption === "Other"
        ? rejectedReason
        : selectedRejectionOption;

      await updateBorrowerOffer({
        offerId,
        status: "rejected",
        borrowerId: userId,
        rejectedReason: finalrejectedReason || ""
      });

      showToast("Offer declined successfully", "info");
      navigate("/accepted-offers");
    } catch (error) {
      showToast("Failed to decline offer", "error");
    } finally {
      setLoading(false);
      setRejectDialogOpen(false);
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setrejectedReason("");
    setSelectedRejectionOption("");
  };

  const handleBack = () => {
    navigate("/accepted-offers");
  };

  const loanFacilities = useMemo(() => {
    if (offerDetails?.facilities?.length) {
      return offerDetails.facilities;
    }
    if (offerDetails) {
      return [
        {
          id: offerDetails.id,
          facilityType: offerDetails.facilityType,
          purpose: offerDetails.purpose,
          loanOffered: offerDetails.loanOffered,
          rateOfIntrest: offerDetails.rateOfIntrest,
          repaymentTenure: offerDetails.repaymentTenure,
          processingFees: offerDetails.processingFees,
          miscellaneousCharges: offerDetails.miscellaneousCharges,
          offeredSecurity: offerDetails.offeredSecurity,
          securityCoveragePercent: offerDetails.securityCoveragePercent,
          emi: offerDetails.emi,
        },
      ];
    }
    return [];
  }, [offerDetails]);

  const offerCreatedDate = offerDetails?.createDatetime || offerDetails?.offerDate;

  const formattedOfferDateText = useMemo(
    () =>
      offerCreatedDate
        ? new Date(offerCreatedDate).toLocaleDateString("en-IN")
        : new Date().toLocaleDateString("en-IN"),
    [offerCreatedDate]
  );

  const borrowerDetails = useMemo(() => {
    const user = userDetail || {};
    return {
      name: user.EntityName || user.Name || "N/A",
      address: [user.AddressLine1, user.City].filter(Boolean).join(", ") || "N/A",
      salutation: user.Name || user.EntityName || "Customer",
    };
  }, [userDetail]);

  const bankDetails = useMemo(() => {
    const displayName =
      lenderDetails?.lender_financialInstitution || offerDetails?.bankName || "N/A";
    const address =
      lenderDetails?.address || offerDetails?.bankAddress || "As per bank records";
    return {
      displayName,
      address,
      pdfName: displayName === "N/A" ? "[Bank/FI Name]" : displayName,
    };
  }, [lenderDetails, offerDetails?.bankName, offerDetails?.bankAddress]);

  const pdfFileName = useMemo(
    () =>
      `Indicative-Sanction-${
        offerDetails?.offerCode || offerDetails?.id || offerId || "offer"
      }.pdf`,
    [offerDetails?.offerCode, offerDetails?.id, offerId]
  );

  const facilityCopy = useMemo(() => {
    const type = loanFacilities[0]?.facilityType || offerDetails?.facilityType;
    return {
      type,
      subjectText: type || "(Specify Loan Type: e.g., Business Loan, Working Capital Loan)",
      narrativeText: type || "[Specify Loan Type]",
    };
  }, [loanFacilities, offerDetails?.facilityType]);

  const { name: borrowerDisplayName, address: borrowerAddress, salutation: borrowerSalutation } =
    borrowerDetails;
  const {
    displayName: bankDisplayName,
    address: bankDisplayAddress,
    pdfName: pdfBankName,
  } = bankDetails;
  const { subjectText: subjectFacilityText, narrativeText: narrativeFacilityText } = facilityCopy;

  const handleDownloadOfferLetter = useCallback(async () => {
    const target = contentRef.current;
    if (!target) return;

    const tables = [...target.querySelectorAll(".MuiTableContainer-root")];
    const originalStyles = tables.map(({ style }) => ({
      style,
      overflow: style.overflow,
      maxHeight: style.maxHeight,
      height: style.height,
    }));

    const setTableStyles = ({ style }, overrides) =>
      Object.assign(style, overrides ?? {
        overflow: "visible",
        maxHeight: "none",
        height: "auto",
      });

    tables.forEach((table, index) => setTableStyles(originalStyles[index]));

    try {
      const pdf = new jsPDF("p", "pt", "a4");
      const canvas = await html2canvas(target, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#FFFFFF",
        logging: false,
      });

      const A4_WIDTH = 595.28;
      const A4_HEIGHT = 841.89;
      const marginX = 20;
      const usableWidth = A4_WIDTH - marginX * 2;
      const scale = Math.min(usableWidth / canvas.width, 1);
      const scaledWidth = canvas.width * scale;
      const scaledHeight = canvas.height * scale;

      const renderSlice = (offset) => {
        const sliceHeight = Math.min(A4_HEIGHT, scaledHeight - offset);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = (sliceHeight / scaledHeight) * canvas.height;
        pageCanvas
          .getContext("2d")
          .drawImage(
            canvas,
            0,
            (offset / scaledHeight) * canvas.height,
            canvas.width,
            pageCanvas.height,
            0,
            0,
            canvas.width,
            pageCanvas.height
          );
        pdf.addImage(
          pageCanvas.toDataURL("image/png"),
          "PNG",
          marginX,
          0,
          scaledWidth,
          sliceHeight
        );
      };

      for (let offset = 0; offset < scaledHeight; offset += A4_HEIGHT) {
        if (offset > 0) pdf.addPage();
        renderSlice(offset);
      }

      pdf.save(pdfFileName);
    } catch (error) {
      showToast("Failed to download offer letter", error);
    } finally {
      originalStyles.forEach(({ style, overflow, maxHeight, height }) =>
        setTableStyles({ style }, { overflow, maxHeight, height })
      );
    }
  }, [pdfFileName]);

  if (loading) {
    return (
      <PageWrapper>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
          <CircularProgress />
        </Box>
      </PageWrapper>
    );
  }

  if (!offerDetails) {
    return (
      <PageWrapper>
        <ContentContainer>
          <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </BackButton>
          <Typography variant="h6" color="error">No offer details found</Typography>
        </ContentContainer>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <ContentContainer>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
            Back
          </BackButton>
          <Button
            variant="outlined"
            onClick={handleDownloadOfferLetter}
            disabled={loading}
            sx={{
              ...commonButtonStyles,
              minWidth: "auto",
              padding: "8px 18px",
              borderColor: "#0C53B7",
              color: "#0C53B7",
              backgroundColor: "rgba(12, 83, 183, 0.04)",
            }}
          >
            Download Offer Letter
          </Button>
        </Box>
        <Box
          ref={contentRef}
          sx={{
            backgroundColor: "#FFFFFF",
            borderRadius: "24px",
            padding: { xs: 3, sm: 4, md: 5 },
            boxShadow: "0 15px 35px rgba(15, 23, 42, 0.08)",
            border: "1px solid rgba(145, 158, 171, 0.18)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {watermarkSrc && (
            <Box
              aria-hidden="true"
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                opacity: 0.14,
                zIndex: 2,
                mixBlendMode: "multiply",
              }}
            >
                <Box
                  component="img"
                  src={watermarkSrc}
                  alt=""
                  sx={{
                    width: { xs: "450px", md: "600px" },
                    maxWidth: "100%",
                    objectFit: "contain",
                    filter: "grayscale(100%)",
                  }}
                />
            </Box>
          )}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
                mb: 2,
              }}
            >
              <Box>
                <PageTitle>Indicative Sanction Letter</PageTitle>
                <PageDescription>
                  This letter outlines the indicative sanction terms shared by the financier.
                </PageDescription>
              </Box>
              {letterheadLogoSrc && (
                <Box
                  component="img"
                  src={letterheadLogoSrc}
                  alt="Dequi Letterhead Logo"
                  sx={{
                    width: { xs: 120, md: 160 },
                    maxWidth: "40%",
                    objectFit: "contain",
                    mixBlendMode: "multiply",
                    opacity: 0.9,
                  }}
                />
              )}
            </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 3,
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={{ ...commonTextStyles, fontSize: "14px", fontWeight: 600 }}>
                Bank/FI Name:{" "}
                <span style={{ fontWeight: 600 }}>
                  {bankDisplayName}
                </span>
              </Typography>
              <Typography
                sx={{
                  ...commonTextStyles,
                  fontSize: "14px",
                  mt: 0.5,
                  display: "inline",
                  mr: 1,
                }}
              >
                Bank/FI Address:
              </Typography>
              <Typography
                sx={{
                  ...commonTextStyles,
                  fontSize: "14px",
                  color: "#637381",
                  display: "inline",
                }}
              >
                {bankDisplayAddress}
              </Typography>
              <Typography sx={{ ...commonTextStyles, fontSize: "14px", mt: 1 }}>
                Date: {formattedOfferDateText}
              </Typography>
            </Box>
            <Box
              sx={{
                minWidth: 120,
                minHeight: 60,
                border: "1px dashed #E0E0E0",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
              }}
            >
              {lenderDetails?.profilePhotoUrl ? (
                <Box
                  component="img"
                  src={lenderDetails.profilePhotoUrl}
                  alt={lenderDetails?.lender_financialInstitution || "Bank/FI Logo"}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
              ) : (
                <Typography
                  sx={{
                    ...commonTextStyles,
                    fontSize: "12px",
                    color: "#637381",
                    textAlign: "center",
                  }}
                >
                  Bank/FI Logo
                </Typography>
              )}
            </Box>
          </Box>

        <Box sx={{ mt: -1.5, mb: 2 }}>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", mb: 1 }}>
            To
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", fontWeight: 600 }}>
            {borrowerDisplayName}
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381" }}>
            {borrowerAddress}
          </Typography>
        </Box>

        <Typography
          sx={{
            ...commonTextStyles,
            fontSize: "14px",
            fontWeight: 600,
            mb: 2,
          }}
        >
          Subject: Indicative Sanction for {subjectFacilityText}
        </Typography>

        <Typography
          sx={{
            ...commonTextStyles,
            fontSize: "14px",
            color: "#637381",
            mb: 2,
          }}
        >
          Dear {borrowerSalutation}
        </Typography>
        <Typography
          sx={{
            ...commonTextStyles,
            fontSize: "14px",
            color: "#637381",
            mb: 3,
          }}
        >
          We are pleased to inform you that based on our initial assessment of your loan
          application and the information provided,&nbsp;
          {pdfBankName} is inclined to sanction a{" "}
          {narrativeFacilityText}{" "}
          facility, subject to the terms and conditions outlined below and further due
          diligence. Please note that this is an indicative sanction only and does not
          constitute a final commitment from {pdfBankName}.
        </Typography>

        <Typography
          sx={{
            ...commonTextStyles,
            fontSize: "14px",
            color: "#637381",
            mt: 3,
            mb: 1.5,
          }}
        >
          The indicative terms of the loan facility are as follows:
        </Typography>

        <SectionTitle>Indicative Facility Structure:</SectionTitle>

        <LoanTableWrapper>
          {loanFacilities.length > 0 ? (
            <TableContainer component={Paper} elevation={0} sx={{ width: "100%" }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <LoanTableCell>Sl No.</LoanTableCell>
                    <LoanTableCell>Facility Type</LoanTableCell>
                    <LoanTableCell>Amount (Lacs)</LoanTableCell>
                    <LoanTableCell>Tenure (Months)</LoanTableCell>
                    <LoanTableCell>ROI</LoanTableCell>
                    <LoanTableCell>EMI in Rs</LoanTableCell>
                    <LoanTableCell>Purpose</LoanTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loanFacilities.map((facility, index) => {
                    const amount = Number(facility.loanOffered || 0);
                    const amountInLacs =
                      amount > 0 ? (amount / 100000).toFixed(2) : "0.00";
                    const roi =
                      facility.rateOfIntrest || facility.rateOfInterest
                        ? `${facility.rateOfIntrest ?? facility.rateOfInterest}%`
                        : "N/A";
                    return (
                      <TableRow
                        key={facility.id || index}
                        hover
                        sx={{
                          "&:last-of-type td": { borderBottom: "none" },
                          "&:hover": {
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <LoanTableCell>{index + 1}</LoanTableCell>
                        <LoanTableCell>
                          {facility.facilityType || `Facility ${index + 1}`}
                        </LoanTableCell>
                        <LoanTableCell>{amountInLacs}</LoanTableCell>
                        <LoanTableCell>
                          {facility.repaymentTenure || facility.tenure
                            ? `${facility.repaymentTenure ?? facility.tenure}`
                            : "N/A"}
                        </LoanTableCell>
                        <LoanTableCell>{roi}</LoanTableCell>
                        <LoanTableCell>
                          {facility.emi
                            ? `${Number(facility.emi).toLocaleString("en-IN")}/-`
                            : "N/A"}
                        </LoanTableCell>
                        <LoanTableCell>{facility.purpose || "N/A"}</LoanTableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <LoanTableCell />
                    <LoanTableCell sx={{ fontWeight: 600 }}>Total</LoanTableCell>
                    <LoanTableCell sx={{ fontWeight: 600 }}>
                      {(() => {
                        const totalAmount = loanFacilities.reduce(
                          (sum, f) => sum + Number(f.loanOffered || 0),
                          0
                        );
                        return totalAmount > 0
                          ? (totalAmount / 100000).toFixed(2)
                          : "0.00";
                      })()}
                    </LoanTableCell>
                    <LoanTableCell />
                    <LoanTableCell />
                    <LoanTableCell />
                    <LoanTableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <LoanTableEmptyState>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Loan details unavailable
              </Typography>
              <Typography variant="body2">
                We could not find any loan facilities for this offer.
              </Typography>
            </LoanTableEmptyState>
          )}
        </LoanTableWrapper>

        <SectionTitle>Indicative Sanction Terms:</SectionTitle>
        <InfoTable>
          <InfoRow container>
            <Grid item xs={6}>
              <InfoLabel>Processing Fees % of Loan amount:</InfoLabel>
            </Grid>
            <Grid item xs={6}>
              <InfoValue>
                {offerDetails.processingFeesPercent ||
                  offerDetails.processingFees ||
                  "N/A"}
              </InfoValue>
            </Grid>
          </InfoRow>
          <InfoRow container>
            <Grid item xs={6}>
              <InfoLabel>Primary Security:</InfoLabel>
            </Grid>
            <Grid item xs={6}>
              <InfoValue>
                {offerDetails.primarySecurity?.trim?.() || "N/A"}
              </InfoValue>
            </Grid>
          </InfoRow>
          <InfoRow container>
            <Grid item xs={6}>
              <InfoLabel>Secondary Security/Collateral:</InfoLabel>
            </Grid>
            <Grid item xs={6}>
              <InfoValue>
                {offerDetails.secondarySecurity || "N/A"}
              </InfoValue>
            </Grid>
          </InfoRow>
          <InfoRow container>
            <Grid item xs={6}>
              <InfoLabel>Guarantee:</InfoLabel>
            </Grid>
            <Grid item xs={6}>
              <InfoValue>
                {offerDetails.proposedPersonalORCorporateGaurantee ||
                  offerDetails.guarantee ||
                  "N/A"}
              </InfoValue>
            </Grid>
          </InfoRow>
          <InfoRow container>
            <Grid item xs={6}>
              <InfoLabel>Specific Conditions 1:</InfoLabel>
            </Grid>
            <Grid item xs={6}>
              <InfoValue>
                {offerDetails.indicativeSpecificCondition1 ||
                  offerDetails.specificCondition1 ||
                  "N/A"}
              </InfoValue>
            </Grid>
          </InfoRow>
          <InfoRow container>
            <Grid item xs={6}>
              <InfoLabel>Specific Conditions 2:</InfoLabel>
            </Grid>
            <Grid item xs={6}>
              <InfoValue>
                {offerDetails.indicativeSpecificCondition2 ||
                  offerDetails.specificCondition2 ||
                  "N/A"}
              </InfoValue>
            </Grid>
          </InfoRow>
          <InfoRow container>
            <Grid item xs={6}>
              <InfoLabel>Specific Conditions 3:</InfoLabel>
            </Grid>
            <Grid item xs={6}>
              <InfoValue>
                {offerDetails.indicativeSpecificCondition3 ||
                  offerDetails.specificCondition3 ||
                  "N/A"}
              </InfoValue>
            </Grid>
          </InfoRow>
        </InfoTable>

        <Typography
          sx={{
            ...commonTextStyles,
            fontSize: "14px",
            color: "#637381",
            mt: 1,
            mb: 1,
          }}
        >
          The final sanction and disbursement are subject to satisfactory completion of
          our internal processes, including but not limited to:
        </Typography>
        <Box sx={{ pl: 2, mb: 1 }}>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381" }}>
            • Detailed verification of all submitted documents.
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381" }}>
            • Satisfactory credit appraisal and risk assessment.
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381" }}>
            • Legal and technical due diligence of any proposed security/collateral (if
            applicable).
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381" }}>
            • Compliance with all internal policies and regulatory requirements.
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381" }}>
            • Execution of the loan agreement and other necessary documents.
          </Typography>
        </Box>

        <SectionTitle>Next Steps</SectionTitle>
        <Typography
          sx={{
            ...commonTextStyles,
            fontSize: "14px",
            color: "#637381",
            mb: 0.5,
          }}
        >
          To proceed further with your loan application, we request you to:
        </Typography>
        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381", mb: 0.5 }}>
            1. Acknowledge your acceptance of these indicative terms, by accepting this
            proposal on Dequitec platform within 5 days from the date of this letter.
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381", mb: 0.5 }}>
            2. Once consented by you we will collect all the KYC and Financial documents
            from Dequitec and start our loan appraisal process.
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381" }}>
            3. Upon satisfactory due diligence we will issue the final sanction letter
            outlining the complete terms and conditions of the loan facility.
          </Typography>
        </Box>

        <Typography
          sx={{
            ...commonTextStyles,
            fontSize: "14px",
            color: "#637381",
            mb: 1.5,
          }}
        >
          We look forward to assisting you with your financing needs.
        </Typography>

        <Typography
          sx={{
            ...commonTextStyles,
            fontSize: "14px",
            color: "#637381",
            mb: 1,
          }}
        >
          Sincerely,
        </Typography>
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", fontWeight: 600 }}>
            {lenderDetails?.name || "(Name of Authorized Bank Representative)"}
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381" }}>
            {lenderDetails?.lender_designation || "(Title)"}
          </Typography>
          <Typography sx={{ ...commonTextStyles, fontSize: "14px", color: "#637381" }}>
            ({offerDetails.bankName || "Bank/FI Name"})
          </Typography>
        </Box>

        </Box>
        </Box>

        {canTakeAction && (
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid rgba(145,158,171,0.25)",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
              }
              label={
                <CheckboxLabel>
                  I have read and agree to the terms and conditions of this loan
                </CheckboxLabel>
              }
            />
            <Box
              sx={{
                mt: 2,
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <AcceptOfferButton
                variant="contained"
                onClick={() => handleOfferAction("accepted")}
                disabled={!termsAccepted || loading}
              >
                Accept Offer
              </AcceptOfferButton>
              <DeclineOfferButton
                variant="outlined"
                onClick={() => handleOfferAction("rejected")}
                disabled={loading}
              >
                Decline Offer
              </DeclineOfferButton>
            </Box>
          </Box>
        )}

        <Dialog
          open={rejectDialogOpen}
          onClose={handleRejectCancel}
          aria-labelledby="reject-dialog-title"
          PaperProps={{
            sx: {
              borderRadius: "12px",
              padding: "16px",
              maxWidth: "400px",
            },
          }}
        >
          <DialogTitle id="reject-dialog-title" sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "20px",
            fontWeight: 600,
            color: "#212B36",
            pb: 1
          }}>
            Decline Offer
          </DialogTitle>
          <DialogContent>
            <Typography sx={{
              mb: 2,
              color: "#637381",
              fontSize: "14px",
              fontFamily: "'Inter', sans-serif"
            }}>
              Please select a reason for declining this offer
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="rejection-reason-label">Reason</InputLabel>
              <Select
                labelId="rejection-reason-label"
                value={selectedRejectionOption}
                onChange={(e) => setSelectedRejectionOption(e.target.value)}
                label="Reason"
              >
                {rejectionOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedRejectionOption === "Other" && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Please specify"
                value={rejectedReason}
                onChange={(e) => setrejectedReason(e.target.value)}
                sx={{ mb: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ padding: "16px" }}>
            <Button
              onClick={handleRejectCancel}
              sx={{
                color: "#637381",
                textTransform: "none",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              variant="contained"
              sx={{
                bgcolor: "#FF4842",
                color: "white",
                textTransform: "none",
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                "&:hover": {
                  bgcolor: "#B72136",
                },
              }}
            >
              Decline Offer
            </Button>
          </DialogActions>
        </Dialog>
      </ContentContainer>
    </PageWrapper>
  );
};

export default ViewProposalDetails;
